import { Col, Form, Row } from "antd";
import React from "react";
import Explain from "../../../../components/atoms/Explain";
import TimePicker from "../../../../components/atoms/TimePicker";
import ExplainWrapper from "../../../../components/molecures/ExplainWrapper";
import sharedConstants from "../../../../shared/constants";
import { theme } from "../../../../theme";
import { ValidationStatus } from "../../../Register/components/types";
import {
  DayOffTime,
  RenderInputProps,
  RestTime,
  TimeMode,
} from "../../Interface";
import { StyledRow, Wave } from "../../styles";
import {
  checkDuplicatedBreakTime,
  makeDisabledTime,
  stringTimeToMoment,
} from "../../utils";
import MinusGhostButton from "../MinusGhostButton";
import PlusTimeTextButton from "../PlusTimeTextButton";

const { Item } = Form;
const HolidayRestInputs: React.FC<RenderInputProps> = ({ form }) => {
  const holidayRestTimes: RestTime = Form.useWatch("holidayRestTimes", form);
  const holidayStartBusinessTime = Form.useWatch(
    ["repairShopBusiness", "holidayStartBusinessTime"],
    form
  );
  const holidayEndBusinessTime = Form.useWatch(
    ["repairShopBusiness", "holidayEndBusinessTime"],
    form
  );

  return (
    <Row gutter={[0, 12]}>
      <Col span={24}>
        <Explain
          color={theme.colors.gray3}
          status="question"
          explain="공휴일 휴게시간이 기본 휴게시간과 다를 경우에만 입력해주세요."
        />
      </Col>
      <Col span={24}>
        <Row gutter={[0, 12]}>
          {holidayRestTimes?.times.map((value, timeIndex) => {
            const checkDuplicated = checkDuplicatedBreakTime({
              target: holidayRestTimes?.times[timeIndex],
              times: [
                ...holidayRestTimes.times.filter(
                  (val, index) => index !== timeIndex
                ),
                {
                  startTime: stringTimeToMoment(holidayStartBusinessTime),
                  endTime: stringTimeToMoment(holidayEndBusinessTime),
                },
              ],
            });
            const getStatus = (): ValidationStatus => {
              if (checkDuplicated.status === "error") return checkDuplicated;
              if (
                (value.endTime && !value.startTime) ||
                (value.startTime && !value.endTime)
              )
                return {
                  status: "error",
                  explain: sharedConstants.message.ALERT_MESSAGE.NULL_REQUIRED,
                };
              return {
                status: undefined,
                explain: undefined,
              };
            };
            return (
              <Col span={24} key={timeIndex}>
                <ExplainWrapper status={getStatus()}>
                  <StyledRow gutter={[12, 0]}>
                    <Col flex="0 0 200px">
                      <Item
                        noStyle
                        name={`holidayRestTimes_startTime_${timeIndex}`}
                        rules={[
                          {
                            validator: () => {
                              if (
                                holidayRestTimes?.times?.[timeIndex].endTime &&
                                !holidayRestTimes?.times?.[timeIndex].startTime
                              )
                                return Promise.reject(
                                  new Error(
                                    sharedConstants.message.ALERT_MESSAGE.NULL_REQUIRED
                                  )
                                );

                              if (checkDuplicated.status === "error")
                                return Promise.reject(
                                  new Error(checkDuplicated.explain)
                                );
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                      <TimePicker
                        popupStyle={{
                          minWidth: "calc(200px - 32px)",
                        }}
                        onOk={(selectedTime) => {
                          const newTimes = [...holidayRestTimes.times];
                          const thisRow = newTimes[timeIndex];
                          thisRow.startTime = selectedTime;
                          form.setFieldsValue({
                            holidayRestTimes: {
                              ...form.getFieldValue("holidayRestTimes"),
                              times: newTimes,
                            },
                          });
                        }}
                        status={getStatus().status ? "error" : undefined}
                        disabledTime={() =>
                          makeDisabledTime({
                            fatestStartTime: stringTimeToMoment(
                              holidayStartBusinessTime
                            ),
                            fatestEndTime: value.endTime,
                            latestEndTime: stringTimeToMoment(
                              holidayEndBusinessTime
                            ),
                            latestStartTime: value.startTime,
                            disabledTimes: holidayRestTimes.times,
                          })
                        }
                        value={value.startTime}
                        endHour={value.endTime?.hours()}
                        type={TimeMode.START}
                      />
                    </Col>
                    <Wave />
                    <Col flex="0 0 200px">
                      <Item
                        noStyle
                        name={`holidayRestTimes_endTime_${timeIndex}`}
                        rules={[
                          {
                            validator: () => {
                              if (
                                holidayRestTimes?.times?.[timeIndex]
                                  .startTime &&
                                !value.endTime
                              )
                                return Promise.reject(
                                  new Error(
                                    sharedConstants.message.ALERT_MESSAGE.NULL_REQUIRED
                                  )
                                );

                              if (checkDuplicated.status === "error")
                                return Promise.reject(
                                  new Error(checkDuplicated.explain)
                                );
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                      <TimePicker
                        popupStyle={{
                          width: "calc(200px - 32px)",
                        }}
                        onOk={(selectedTime) => {
                          const newTimes = [...holidayRestTimes.times];
                          const thisRow = newTimes[timeIndex];
                          thisRow.endTime = selectedTime;
                          form.setFieldsValue({
                            holidayRestTimes: {
                              ...form.getFieldValue("holidayRestTimes"),
                              times: newTimes,
                            },
                          });
                        }}
                        status={getStatus().status ? "error" : undefined}
                        disabledTime={() =>
                          makeDisabledTime({
                            fatestStartTime: stringTimeToMoment(
                              holidayStartBusinessTime
                            ),
                            fatestEndTime: value.endTime,
                            latestEndTime: stringTimeToMoment(
                              holidayEndBusinessTime
                            ),
                            mode: TimeMode.END,
                            latestStartTime: value.startTime,
                            disabledTimes: holidayRestTimes.times,
                          })
                        }
                        value={value.endTime}
                        startHour={value.startTime?.hours()}
                        type={TimeMode.END}
                      />
                    </Col>
                    {holidayRestTimes.times?.length > 0 && (
                      <Col>
                        <MinusGhostButton
                          onClick={() => {
                            const newTimes = [...holidayRestTimes.times];
                            form.setFieldsValue({
                              holidayRestTimes: {
                                ...form.getFieldValue("holidayRestTimes"),
                                times: newTimes.filter(
                                  (val, index) => index !== timeIndex
                                ),
                              },
                            });
                          }}
                        />
                      </Col>
                    )}
                  </StyledRow>
                </ExplainWrapper>
              </Col>
            );
          })}

          <Col span={24}>
            <PlusTimeTextButton
              arrayLength={holidayRestTimes?.times?.length}
              onClick={() => {
                const newTime: DayOffTime = {
                  startTime: undefined,
                  endTime: undefined,
                };
                if (holidayRestTimes?.times) {
                  const newTimes = [...holidayRestTimes?.times];

                  form.setFieldsValue({
                    holidayRestTimes: {
                      ...form.getFieldValue("holidayRestTimes"),
                      times: [...newTimes, newTime],
                    },
                  });
                }
              }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default HolidayRestInputs;
